import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const LEAVE_TYPES = [
  { id: '연차', label: '연차', desc: '연차 유급 휴가', unit: '반반차(0.25)', paid: true },
  { id: '출산휴가', label: '출산휴가', desc: '임신, 출산 등으로 인한 체력회복을 위한 산전 후 유급휴가', unit: '일', paid: true },
  { id: '출산휴가-다태아', label: '출산휴가-다태아', desc: '임신, 출산 등으로 인한 체력회복을 위한 산전 후 유급휴가', unit: '일', paid: true },
  { id: '배우자돌봄휴가', label: '배우자돌봄휴가', desc: '배우자가 출산 후 배우자와 태아의 건강을 위한 휴가', unit: '일', paid: true },
  { id: '가족돌봄휴가', label: '가족돌봄휴가', desc: '질병, 사고, 양육 등의 이유로 가족을 돌보기 위한 휴가', unit: '일', paid: false },
  { id: '경조사', label: '경조사', desc: '경조사로 인한 휴가', unit: '일', paid: true },
  { id: '공가', label: '공가', desc: '공적인 사유로 인한 휴가', unit: '부분', paid: true },
];

const STATUS_MAP = { pending: { label: '대기', color: '#f59e0b', bg: '#fef3c7' }, approved: { label: '승인', color: '#059669', bg: '#d1fae5' }, rejected: { label: '반려', color: '#dc2626', bg: '#fee2e2' } };

export default function LeaveMain() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [tab, setTab] = useState('status');
  const tabs = [
    { id: 'status', label: '휴가현황' },
    { id: 'history', label: '연차내역' },
    { id: 'dept', label: '부서 휴가현황' },
  ];

  useEffect(() => {
    if (!user) return;
    fetchStats();
    fetchLeaves();
  }, [user]);

  const fetchStats = async () => {
    try {
      const r = await fetch(`/api/leaves/stats/${user?.id}?year=${new Date().getFullYear()}`);
      setStats(await r.json());
    } catch (e) {}
  };

  const fetchLeaves = async () => {
    try {
      const r = await fetch(`/api/leaves?userId=${user?.id}`);
      setLeaves(await r.json());
    } catch (e) {}
  };

  const upcoming = leaves.filter(l => l.status === 'pending' || (l.status === 'approved' && l.dates?.some(d => new Date(d.date) >= new Date())));
  const past = leaves.filter(l => l.status === 'approved' && l.dates?.every(d => new Date(d.date) < new Date()));

  return (
    <div className="page container">
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f3f4f6', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t.id}
            onClick={() => t.id === 'history' ? navigate('/leave/history') : t.id === 'dept' ? navigate('/leave/department') : setTab(t.id)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14,
              fontWeight: tab === t.id ? 600 : 400,
              background: tab === t.id ? '#fff' : 'transparent',
              color: tab === t.id ? '#4f46e5' : '#6b7280' }}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">휴가현황</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/leave/new')}>+ 휴가 신청</button>
      </div>

      {/* 연차 현황 */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>연차 현황</h2>
        <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
            {new Date().getFullYear()}-01-01 ~ {new Date().getFullYear()}-12-31 · 회계연도 기준
          </div>
          {stats?.totalAnnual === 0
            ? <div style={{ color: '#9ca3af', fontSize: 13 }}>아직 발생한 연차가 없습니다.</div>
            : <div style={{ fontSize: 13, color: '#374151' }}>총 {stats?.totalAnnual || 0}일 연차 발생</div>
          }
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[
            { label: '잔여 연차', value: `${stats?.remainingDays || 0}d`, sub: `(${(stats?.remainingDays || 0) * 8}h 0m)`, color: '#4f46e5' },
            { label: '사용 연차', value: `${stats?.usedDays || 0}d`, sub: `(${(stats?.usedDays || 0) * 8}h 0m)` },
            { label: '총 연차', value: `${stats?.totalAnnual || 0}d`, sub: `(${(stats?.totalAnnual || 0) * 8}h 0m)` },
            { label: '근속연수', value: `${stats?.yearsWorked || 0}년차`, sub: '' },
          ].map(item => (
            <div key={item.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: item.color || '#374151' }}>{item.value}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.sub}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 휴가 유형 카드 */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>휴가신청</h2>
          <button style={{ fontSize: 13, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>간략히보기 ∧</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {LEAVE_TYPES.map(t => (
            <div key={t.id} onClick={() => navigate('/leave/new', { state: { leaveType: t.id } })}
              style={{ padding: 16, border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#f8f7ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{t.label}</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6, lineHeight: 1.4 }}>{t.desc}</div>
              {!t.paid && <span style={{ fontSize: 11, color: '#dc2626', background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>무급</span>}
              {t.id === '연차' && stats && <div style={{ fontSize: 12, color: '#4f46e5', marginTop: 4 }}>잔여 연차 {stats.remainingDays || 0}d ({(stats.remainingDays || 0) * 8}h 0m)</div>}
            </div>
          ))}
        </div>
      </div>

      {/* 예정/지난 휴가 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>예정휴가</h3>
          {upcoming.length === 0
            ? <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '24px 0' }}>예정된 휴가가 없습니다.</div>
            : upcoming.map(l => {
              const s = STATUS_MAP[l.status] || STATUS_MAP.pending;
              return (
                <div key={l.id} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                  <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{l.leaveType}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{l.totalDays}d · {l.dateRange}</div>
                  </div>
                </div>
              );
            })
          }
        </div>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>지난휴가</h3>
            <button style={{ fontSize: 12, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer' }}>더보기 →</button>
          </div>
          {past.length === 0
            ? <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '24px 0' }}>지난 휴가가 없습니다.</div>
            : past.slice(0, 5).map(l => (
              <div key={l.id} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500, background: '#d1fae5', color: '#059669' }}>승인</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{l.leaveType}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{l.totalDays}d · {l.dateRange}</div>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}
